import React, { useState, useEffect } from 'react';

// Preset options, split into two labelled groups in the dropdown.
export const PRESET_EXCHANGES = [
  'Binance', 'Coinbase', 'Kraken', 'OKX', 'Bybit', 'MEXC', 'Cash App', 'PayPal',
];
export const PRESET_WALLET_APPS = [
  'MetaMask', 'Ledger', 'Trezor', 'Trust Wallet', 'Personal Wallet', "D'CENT Wallet",
];

// Combined list — kept for membership checks / derived-wallet logic elsewhere.
export const PRESET_WALLETS = [...PRESET_EXCHANGES, ...PRESET_WALLET_APPS];

const SELECT_CLASS =
  'w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-slate-500 transition-colors cursor-pointer [color-scheme:dark]';

const INPUT_CLASS =
  'flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-slate-500 transition-colors';

interface WalletSelectorProps {
  value: string;
  onChange: (v: string) => void;
  /**
   * Wallet names the user has actually used (derived from their transactions).
   * Surfaced in a "Your wallets" group at the top and de-duplicated out of the
   * preset groups below. Omit / empty → no "Your wallets" group is shown.
   */
  usedWallets?: string[];
}

export function WalletSelector({ value, onChange, usedWallets = [] }: WalletSelectorProps) {
  const isPreset = value === '' || PRESET_WALLETS.includes(value);
  const [showCustom, setShowCustom] = useState(!isPreset);
  const [customText, setCustomText] = useState(isPreset ? '' : value);

  // When parent resets to '' (e.g. after form submit), switch back to dropdown
  useEffect(() => {
    if (value === '' || PRESET_WALLETS.includes(value)) {
      setShowCustom(false);
    }
  }, [value]);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (v === '__custom__') {
      setShowCustom(true);
      setCustomText('');
    } else {
      onChange(v);
    }
  };

  const handleCancelCustom = () => {
    setShowCustom(false);
    setCustomText('');
    onChange('');
  };

  if (showCustom) {
    return (
      <div className="flex gap-2">
        <input
          type="text"
          value={customText}
          onChange={(e) => { setCustomText(e.target.value); onChange(e.target.value); }}
          placeholder="Enter wallet name…"
          autoFocus
          className={INPUT_CLASS}
        />
        <button
          type="button"
          onClick={handleCancelCustom}
          className="px-3 py-2.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl transition-colors whitespace-nowrap"
        >
          Cancel
        </button>
      </div>
    );
  }

  // Distinct, non-empty used wallets for the "Your wallets" group. Anything shown
  // here is removed from the preset groups below so it never appears twice.
  const yourWallets = Array.from(
    new Set(usedWallets.map((w) => w.trim()).filter(Boolean))
  );
  const usedSet = new Set(yourWallets);
  const exchanges = PRESET_EXCHANGES.filter((w) => !usedSet.has(w));
  const walletApps = PRESET_WALLET_APPS.filter((w) => !usedSet.has(w));

  return (
    <select value={value} onChange={handleSelectChange} className={SELECT_CLASS}>
      <option value="">Unspecified</option>
      {yourWallets.length > 0 && (
        <optgroup label="Your wallets">
          {yourWallets.map((w) => (
            <option key={w} value={w}>{w}</option>
          ))}
        </optgroup>
      )}
      {exchanges.length > 0 && (
        <optgroup label="Exchanges">
          {exchanges.map((w) => (
            <option key={w} value={w}>{w}</option>
          ))}
        </optgroup>
      )}
      {walletApps.length > 0 && (
        <optgroup label="Wallets">
          {walletApps.map((w) => (
            <option key={w} value={w}>{w}</option>
          ))}
        </optgroup>
      )}
      <option value="__custom__">+ Add Custom…</option>
    </select>
  );
}
